import { useEffect, useState } from 'react';
import AdminLayout from '@/components/feature/AdminLayout';
import {
  fetchFinancialReport,
  fetchReportSummary,
  fetchLineShipmentReport,
  type FinancialReport,
  type ReportSummary,
  type LineShipmentReport,
} from '@/services/reports';

function fmtMoney(v: number): string {
  return `$${v.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
}

function StatRow({ label, value, tone = 'neutral' }: { label: string; value: string; tone?: 'danger' | 'success' | 'neutral' }) {
  const color = tone === 'danger' ? 'text-red-600' : tone === 'success' ? 'text-emerald-600' : 'text-foreground-950';
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-sm text-foreground-600">{label}</span>
      <span className={`text-sm font-semibold ${color}`}>{value}</span>
    </div>
  );
}

export default function ReportsPage() {
  const [report, setReport] = useState<FinancialReport | null>(null);
  const [summary, setSummary] = useState<ReportSummary | null>(null);
  const [lineReport, setLineReport] = useState<LineShipmentReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  async function load() {
    setLoading(true);
    setError('');
    try {
      const [r, s, l] = await Promise.all([
        fetchFinancialReport(),
        fetchReportSummary(),
        fetchLineShipmentReport(),
      ]);
      setReport(r);
      setSummary(s);
      setLineReport(l);
      if (!r && !s) setError('No fue posible cargar los reportes. Verifica la conexión.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  return (
    <AdminLayout>
      <div className="p-4 lg:p-6 space-y-5 max-w-[1400px] mx-auto">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-xl lg:text-2xl font-bold text-foreground-950">Reportes</h1>
            <p className="text-sm text-foreground-500 mt-0.5">Resumen financiero calculado en vivo desde las ventas, gastos y movimientos.</p>
          </div>
          <button
            onClick={load}
            className="text-sm px-4 py-2.5 border border-background-300 text-foreground-700 rounded-lg hover:bg-background-100 transition-colors whitespace-nowrap font-medium cursor-pointer"
          >
            <i className="ri-refresh-line mr-1.5"></i>Actualizar
          </button>
        </div>

        {error && (
          <div className="flex items-center gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-4 py-3">
            <i className="ri-error-warning-line"></i>
            <span>{error}</span>
          </div>
        )}

        {loading ? (
          <div className="text-center py-20 text-foreground-400">
            <i className="ri-loader-4-line animate-spin text-3xl mb-3 block"></i>
            Cargando reportes...
          </div>
        ) : (
          <>
            {/* Indicadores de negocio */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              {[
                { label: 'Ventas', value: summary ? String(summary.salesCount) : '—', icon: 'ri-money-dollar-circle-line' },
                { label: 'Pedidos', value: summary ? String(summary.orderCount) : '—', icon: 'ri-shopping-bag-3-line' },
                { label: 'Clientes', value: summary ? String(summary.customerCount) : '—', icon: 'ri-user-heart-line' },
                { label: 'Productos', value: summary ? String(summary.productCount) : '—', icon: 'ri-archive-line' },
              ].map((kpi) => (
                <div key={kpi.label} className="bg-white rounded-xl border border-background-200 p-4">
                  <div className="flex items-center gap-2 text-xs text-foreground-500">
                    <i className={`${kpi.icon} text-accent-500`}></i>
                    {kpi.label}
                  </div>
                  <p className="text-2xl font-bold text-foreground-950 mt-2">{kpi.value}</p>
                </div>
              ))}
            </div>

            {/* P&L */}
            {!report && (
              <div className="flex items-center gap-3 bg-amber-50 border border-amber-200 rounded-xl p-4">
                <i className="ri-error-warning-line text-amber-600 text-lg"></i>
                <p className="text-sm text-amber-800">
                  No pudimos cargar el Estado de Resultados. Revisa la conexión con la base de datos e inténtalo de nuevo.
                </p>
                <button
                  onClick={load}
                  className="ml-auto text-sm px-4 py-2 border border-amber-300 text-amber-800 rounded-lg hover:bg-amber-100 transition-colors whitespace-nowrap font-medium cursor-pointer"
                >
                  <i className="ri-refresh-line mr-1.5"></i>Reintentar
                </button>
              </div>
            )}
            {report && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div className="bg-white rounded-xl border border-background-200 p-5">
                  <h4 className="text-sm font-semibold text-foreground-950 mb-4">Estado de resultados (P&L)</h4>
                  <div className="space-y-3">
                    <div className="flex justify-between text-sm">
                      <span className="text-foreground-600">Ventas brutas</span>
                      <span className="font-semibold text-foreground-950">{fmtMoney(report.grossSales)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-foreground-600">Ingresos por envíos cobrados</span>
                      <span className="font-medium text-emerald-600">+{fmtMoney(report.shippingCollected)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-foreground-600">Costo de ventas</span>
                      <span className="text-foreground-700">-{fmtMoney(report.costTotal)}</span>
                    </div>
                    <div className="flex justify-between text-sm border-t border-background-100 pt-3">
                      <span className="font-semibold text-foreground-900">Utilidad bruta</span>
                      <span className="font-semibold text-emerald-600">{fmtMoney(report.grossProfit)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-foreground-600">Devoluciones</span>
                      <span className="text-foreground-700">-{fmtMoney(report.returns)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-foreground-600">Gastos operativos</span>
                      <span className="text-foreground-700">-{fmtMoney(report.expenses)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-foreground-600">Costo operativo de envíos (Interrapidísimo)</span>
                      <span className="text-foreground-700">-{fmtMoney(report.shippingOperationalCost)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-foreground-600">Comisión contra entrega (recaudo COD)</span>
                      <span className="text-foreground-700">-{fmtMoney(report.codFeesCost)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-foreground-600">Publicidad</span>
                      <span className="text-foreground-700">-{fmtMoney(report.advertising)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-foreground-600">Comisiones pagadas</span>
                      <span className="text-foreground-700">-{fmtMoney(report.commissions)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-foreground-600">Donaciones</span>
                      <span className="text-foreground-700">-{fmtMoney(report.donations)}</span>
                    </div>
                    <div className="flex justify-between text-sm border-t border-background-100 pt-3">
                      <span className="font-semibold text-foreground-900">Utilidad neta</span>
                      <span className={`font-bold ${report.netProfit >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                        {fmtMoney(report.netProfit)}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-xl border border-background-200 p-5">
                  <h4 className="text-sm font-semibold text-foreground-950 mb-4">Márgenes</h4>
                  <div className="space-y-4">
                    <div>
                      <div className="flex justify-between text-sm mb-1.5">
                        <span className="text-foreground-600">Margen bruto</span>
                        <span className="font-semibold text-foreground-950">{report.grossMargin.toFixed(1)}%</span>
                      </div>
                      <div className="w-full h-2 bg-background-100 rounded-full overflow-hidden">
                        <div className="h-full bg-accent-500 rounded-full" style={{ width: `${Math.min(100, report.grossMargin)}%` }}></div>
                      </div>
                    </div>
                    <div>
                      <div className="flex justify-between text-sm mb-1.5">
                        <span className="text-foreground-600">Margen neto</span>
                        <span className={`font-semibold ${report.netMargin >= 0 ? 'text-foreground-950' : 'text-red-600'}`}>{report.netMargin.toFixed(1)}%</span>
                      </div>
                      <div className="w-full h-2 bg-background-100 rounded-full overflow-hidden">
                        <div className={`h-full rounded-full ${report.netMargin >= 0 ? 'bg-emerald-500' : 'bg-red-500'}`} style={{ width: `${Math.min(100, Math.abs(report.netMargin))}%` }}></div>
                      </div>
                    </div>
                    <div className="flex justify-between text-sm pt-3 border-t border-background-100">
                      <span className="text-foreground-600">Pagos aprobados</span>
                      <span className="font-semibold text-foreground-950">{fmtMoney(report.paidPayments)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-foreground-600">Ventas registradas</span>
                      <span className="font-semibold text-foreground-950">{report.salesCount}</span>
                    </div>
                  </div>
                </div>
              </div>
            )}
            {report && (
              <div className="bg-white rounded-xl border border-background-200 p-5">
                <div className="flex items-center gap-2 mb-4">
                  <i className="ri-megaphone-line text-accent-500 text-lg"></i>
                  <h4 className="text-sm font-semibold text-foreground-950">Marketing</h4>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-foreground-500">Gasto / Inversión total</p>
                    <p className="text-2xl font-bold text-foreground-950 mt-1">{fmtMoney(report.marketingInvestment)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-foreground-500">Campañas registradas</p>
                    <p className="text-2xl font-bold text-foreground-950 mt-1">{report.marketingCampaigns}</p>
                  </div>
                </div>
                <p className="text-xs text-foreground-400 mt-3">
                  Vista consolidada. El detalle operativo (conversaciones, CPA, ROAS y % de cierre) se gestiona en el módulo de Marketing.
                </p>
              </div>
            )}
            {!loading && (
              <div className="bg-white rounded-xl border border-background-200 p-5">
                <div className="flex items-center gap-2 mb-1">
                  <i className="ri-truck-line text-accent-500 text-lg"></i>
                  <h4 className="text-sm font-semibold text-foreground-950">Envíos y contraentrega por línea</h4>
                </div>
                <p className="text-xs text-foreground-400 mb-4">
                  Discriminado por línea comercial, contemplando los pedidos mixtos. Las{' '}
                  <strong>babuchas sencillas</strong> cobran el envío al cliente; las <strong>premium</strong> lo
                  llevan incluido y no lo cobran. La comisión es la que cobra Interrapidísimo por recaudar el pago
                  contra entrega.
                </p>

                {!lineReport ? (
                  <div className="flex items-center gap-3 bg-amber-50 border border-amber-200 rounded-xl p-4">
                    <i className="ri-error-warning-line text-amber-600 text-lg"></i>
                    <p className="text-sm text-amber-800">No pudimos cargar el desglose por línea.</p>
                    <button
                      onClick={load}
                      className="ml-auto text-sm px-4 py-2 border border-amber-300 text-amber-800 rounded-lg hover:bg-amber-100 transition-colors whitespace-nowrap font-medium cursor-pointer"
                    >
                      <i className="ri-refresh-line mr-1.5"></i>Reintentar
                    </button>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    {/* Premium */}
                    <div className="border border-background-200 rounded-xl overflow-hidden">
                      <div className="flex items-center gap-2.5 px-4 py-3 bg-background-50 border-b border-background-100">
                        <span className="w-8 h-8 rounded-full bg-accent-100 flex items-center justify-center flex-shrink-0">
                          <i className="ri-vip-crown-line text-accent-600 text-base"></i>
                        </span>
                        <div>
                          <p className="text-sm font-semibold text-foreground-950">Babuchas premium</p>
                          <p className="text-[11px] text-foreground-400">Envío incluido, no se cobra al cliente</p>
                        </div>
                      </div>
                      <div className="p-4 space-y-3">
                        <StatRow label="Costo de envíos" value={fmtMoney(lineReport.premium.shippingCost)} tone="danger" />
                        <StatRow label="Contra entregas" value={String(lineReport.premium.codDeliveries)} />
                        <StatRow label="Comisión contraentrega" value={fmtMoney(lineReport.premium.codCommission)} tone="danger" />
                      </div>
                    </div>

                    {/* Sencilla */}
                    <div className="border border-background-200 rounded-xl overflow-hidden">
                      <div className="flex items-center gap-2.5 px-4 py-3 bg-background-50 border-b border-background-100">
                        <span className="w-8 h-8 rounded-full bg-secondary-100 flex items-center justify-center flex-shrink-0">
                          <i className="ri-footprint-line text-secondary-800 text-base"></i>
                        </span>
                        <div>
                          <p className="text-sm font-semibold text-foreground-950">Babuchas sencillas</p>
                          <p className="text-[11px] text-foreground-400">El envío sí se cobra al cliente</p>
                        </div>
                      </div>
                      <div className="p-4 space-y-3">
                        <StatRow label="Costo de envíos" value={fmtMoney(lineReport.sencilla.shippingCost)} tone="danger" />
                        <StatRow label="Contra entregas" value={String(lineReport.sencilla.codDeliveries)} />
                        <StatRow label="Comisión contraentrega" value={fmtMoney(lineReport.sencilla.codCommission)} tone="danger" />
                        <StatRow label="Recargo cobrado al cliente" value={`+${fmtMoney(lineReport.sencilla.surchargeCollected)}`} tone="success" />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </AdminLayout>
  );
}